import { TeachCard } from "./TeachCard"
import { MultipleChoiceCard } from "./MultipleChoiceCard"
import { TrueFalseCard } from "./TrueFalseCard"
import { FillBlankCard } from "./FillBlankCard"
import { CodeOutputCard } from "./CodeOutputCard"
import { CodeSelectCard } from "./CodeSelectCard"
import { OrderStepsCard } from "./OrderStepsCard"

export interface TeachCardData {
  title: string
  body: string
  keyInsight: string
  code?: string
  language?: string
  template?: string
  initialData?: Record<string, unknown>
  animationSteps?: Record<string, unknown>[]
}

export interface MultipleChoiceCardData {
  question: string
  options: string[]
  correctIndex: number
  explanation: string
}

export interface TrueFalseCardData {
  statement: string
  correct: boolean
  explanation: string
}

export interface FillBlankCardData {
  template: string
  blanks: Array<{ options: string[]; correctIndex: number }>
  explanation: string
}

export interface CodeOutputCardData {
  code: string
  language: string
  options: string[]
  correctIndex: number
  explanation: string
}

export interface CodeSelectCardData {
  question: string
  options: Array<{ code: string; language: string }>
  correctIndex: number
  explanation: string
}

export interface OrderStepsCardData {
  question: string
  steps: string[]
  correctOrder: number[]
  explanation: string
}

export type CardData =
  | { type: "teach"; data: TeachCardData }
  | { type: "multiple_choice"; data: MultipleChoiceCardData }
  | { type: "true_false"; data: TrueFalseCardData }
  | { type: "fill_blank"; data: FillBlankCardData }
  | { type: "code_output"; data: CodeOutputCardData }
  | { type: "code_select"; data: CodeSelectCardData }
  | { type: "order_steps"; data: OrderStepsCardData }

type CardRendererProps = CardData & {
  onAnswer?: (correct: boolean) => void
}

/** Accept raw DB card data (type + JSONB data) without requiring the caller to narrow first */
export function CardRendererRaw({ type, data, onAnswer }: {
  type: string
  data: Record<string, unknown>
  onAnswer?: (correct: boolean) => void
}) {
  return <CardRenderer {...{ type, data } as unknown as CardData} onAnswer={onAnswer} />
}

export function CardRenderer(props: CardRendererProps) {
  const handleAnswer = props.onAnswer ?? (() => {})

  switch (props.type) {
    case "teach":
      return <TeachCard data={props.data} />
    case "multiple_choice":
      return <MultipleChoiceCard data={props.data} onAnswer={handleAnswer} />
    case "true_false":
      return <TrueFalseCard data={props.data} onAnswer={handleAnswer} />
    case "fill_blank":
      return <FillBlankCard data={props.data} onAnswer={handleAnswer} />
    case "code_output":
      return <CodeOutputCard data={props.data} onAnswer={handleAnswer} />
    case "code_select":
      return <CodeSelectCard data={props.data} onAnswer={handleAnswer} />
    case "order_steps":
      return <OrderStepsCard data={props.data} onAnswer={handleAnswer} />
    default:
      return (
        <div className="rounded-lg border border-border bg-muted p-6 text-center text-sm text-muted-foreground">
          Unknown card type: {(props as { type: string }).type}
        </div>
      )
  }
}
